/**
 * Live Preview
 * Optimistically updates DOM elements as the user types translations.
 */

import type { ContentUnit } from '../../content/types.js';
import { HTMLSerializer } from '../../content/serializer/HTMLSerializer.js';

const htmlSerializer = new HTMLSerializer();

/**
 * Update a single element's content with a new translation.
 * Writes to core cache and updates the DOM without full page retranslate.
 */
export function applyLivePreview(
  element: Element,
  text: string,
  styles: Record<string, string[]>,
  key: string,
  locale: string,
  namespace: string,
  sourceText?: string,
  rowState?: string,
): void {
  // Save original HTML before first edit
  if (!element.hasAttribute('data-lr-original')) {
    element.setAttribute('data-lr-original', element.innerHTML);
  }

  // Update core cache if available
  const lr = (window as { LionRapid?: { autoTranslator?: { core?: { cache?: { set?: (k: string, v: string, s?: Record<string, string[]>) => void } } } } }).LionRapid;
  const cache = (lr as Record<string, unknown>)?.autoTranslator
    ? getCache()
    : null;
  if (cache) {
    const fullKey = `${locale}:${namespace}:${key}`;
    cache.set(fullKey, text, styles);
  }

  // Update DOM. Three branches:
  //
  //   1. Translation has its own indexed markers (`<1>...</1>`) → serialize
  //      normally; the serializer re-wraps each marker with its raw style.
  //      Translators position markers freely (Finnish "Valitse" wrapping the
  //      whole word; Spanish "<1>aquí</1>" wrapping just one word) and this
  //      branch renders whatever they chose.
  //
  //   2. Translation is plain AND the row is still machine-translated AND the
  //      source was entirely wrapped in one indexed tag → MT silently dropped
  //      the marker that was in the source. Auto-wrap to rescue the wrapper
  //      so links survive the MT step. Gated on `machine_translated` so that
  //      a human who deliberately typed plain text (rowState='edited') has
  //      their choice respected — we don't put a link back they removed.
  //
  //   3. Anything else → write plain textContent. The wrapper, if any, is
  //      gone — that's either by design (human chose plain) or unavoidable
  //      (mixed inline content the regex above can't reconstruct).
  const hasIndexedMarkers = /<\/?[0-9]+\/?>/.test(text);
  const isMachineTranslated = rowState === 'machine_translated';
  const fullyWrapped = sourceText
    ? sourceText.match(/^<(\d+)>([\s\S]*)<\/\1>$/)
    : null;

  if (hasIndexedMarkers) {
    const unit: ContentUnit = { text, styles };
    element.innerHTML = htmlSerializer.serialize(unit);
  } else if (isMachineTranslated && fullyWrapped && styles && Object.keys(styles).length > 0) {
    const idx = fullyWrapped[1];
    const wrappedText = `<${idx}>${text}</${idx}>`;
    const unit: ContentUnit = { text: wrappedText, styles };
    element.innerHTML = htmlSerializer.serialize(unit);
  } else {
    element.textContent = text;
  }

  // Mark as translated
  element.setAttribute('data-lr-block', key);
  element.setAttribute('data-lionrapid-client', '1');
}

function getCache(): { set: (key: string, value: string, styles?: Record<string, string[]>) => void } | null {
  try {
    const lr = (window as unknown as { LionRapid?: Record<string, unknown> }).LionRapid;
    if (!lr) return null;
    const at = lr.autoTranslator as Record<string, unknown> | undefined;
    if (!at) return null;
    const core = at.core as Record<string, unknown> | undefined;
    if (!core) return null;
    const cache = core.cache as { set?: (key: string, value: string, styles?: Record<string, string[]>) => void } | undefined;
    if (!cache || typeof cache.set !== 'function') return null;
    return cache as { set: (key: string, value: string, styles?: Record<string, string[]>) => void };
  } catch {
    return null;
  }
}
